import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Factory, Clock, Shield, Users, CheckCircle, ArrowRight } from 'lucide-react';

const QuiSommesNousPage = () => {
  const timeline = [
    { year: '1986', event: 'Création de Stores & Pergolas™ dans le sud de la France' },
    { year: '1995', event: 'Ouverture de notre usine de 2000m² à Gardanne (13)' },
    { year: '2005', event: 'Partenariat officiel avec Somfy® et Llaza®' },
    { year: '2015', event: 'Lancement de la gamme Pergolas Bioclimatiques' },
    { year: '2020', event: 'Obtention de la certification Garantie 12 ans' },
    { year: '2024', event: 'Plus de 50 000 stores installés en France' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#1a1a1a] py-14">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-3">
            <Link to="/" className="hover:text-orange-400 transition-colors">Accueil</Link>
            <span>/</span>
            <span className="text-white">Qui sommes-nous ?</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-white mb-3">
            Un parti pris de <span className="text-orange-500">qualité</span>
          </h1>
          <p className="text-gray-400 max-w-2xl text-lg">
            Fabricant français de stores et pergolas sur mesure depuis 1986 dans les Bouches du Rhône.
          </p>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 py-14">
        {/* Intro */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <span className="inline-block bg-orange-100 text-orange-600 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded mb-4">
              Notre histoire
            </span>
            <h2 className="text-3xl font-black text-gray-900 mb-5">
              Spécialiste et fabricant français depuis <span className="text-orange-500">1986</span>
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              C'est dans le sud de la France, entre d'Aix en Provence et Marseille, que sont fabriqués
              nos stores bannes extérieurs de haute qualité à prix d'usine.
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              Nous fabriquons des stores en utilisant de l'aluminium « Première Fusion », des toiles
              100% acrylique, une visserie en Inox. En matière d'électronique, nous privilégions
              fiabilité et réparabilité en sélectionnant les produits de la maison SOMFY™ à Cluzes (74).
            </p>
            <p className="text-gray-600 leading-relaxed mb-6">
              La majorité des pièces de fonderie sont achetées auprès de l'entreprise LLAZA™ de
              Perpignan (66). La majorité des produits que commercialise Stores & Pergolas™ sont
              garantis 12 ans, ce qui est une preuve de qualité.
            </p>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Factory, text: 'Usine 2000m² à Gardanne' },
                { icon: Shield, text: 'Garantie 12 ans' },
                { icon: Users, text: 'Equipe dédiée' },
                { icon: Clock, text: 'Depuis 1986' },
              ].map(({ icon: Icon, text }, i) => (
                <div key={i} className="flex items-center gap-2.5 p-3 bg-white rounded-lg border border-gray-100 shadow-sm">
                  <Icon size={18} className="text-orange-500" />
                  <span className="text-sm font-semibold text-gray-700">{text}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-xl">
            <img
              src="https://stores-shop.com/wp-content/uploads/2020/01/store-de-terrasse3-1.jpg"
              alt="Notre usine Gardanne"
              className="w-full h-96 object-cover"
              onError={(e) => { e.target.src = 'https://images.pexels.com/photos/14212807/pexels-photo-14212807.jpeg?w=800'; }}
            />
          </div>
        </div>

        {/* Timeline */}
        <div className="mb-16">
          <h2 className="text-2xl font-black text-gray-900 mb-8 text-center">Notre histoire en dates</h2>
          <div className="relative">
            <div className="absolute left-1/2 -translate-x-px top-0 bottom-0 w-0.5 bg-gray-200 hidden md:block" />
            <div className="space-y-6">
              {timeline.map((item, i) => (
                <div key={i} className={`flex items-center gap-6 ${ i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse' }`}>
                  <div className="flex-1">
                    <div className={`bg-white rounded-xl p-5 shadow-sm border border-gray-100 ${ i % 2 === 0 ? 'md:text-right' : 'md:text-left' }`}>
                      <p className="text-gray-600 text-sm">{item.event}</p>
                    </div>
                  </div>
                  <div className="flex-shrink-0 w-14 h-14 bg-orange-500 rounded-full flex items-center justify-center shadow-lg z-10">
                    <span className="text-white text-xs font-black">{item.year}</span>
                  </div>
                  <div className="flex-1 hidden md:block" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Values */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
          <h2 className="text-2xl font-black text-gray-900 mb-8 text-center">Nos valeurs</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Award, title: 'Qualité sans compromis', desc: 'Aluminium Première Fusion, toile 100% acrylique, visserie Inox. Chaque composant est choisi pour sa durabilité.' },
              { icon: Factory, title: 'Fabriqué en France', desc: 'Nos stores sont fabriqués dans notre usine de 2000m² à Gardanne dans les Bouches du Rhône (13).' },
              { icon: Shield, title: 'Garantie 12 ans', desc: 'La majorité de nos produits sont garantis 12 ans. C’est notre engagement de qualité envers vous.' },
            ].map(({ icon: Icon, title, desc }, i) => (
              <div key={i} className="text-center p-6">
                <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Icon size={28} className="text-orange-500" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-4 rounded-xl transition-all duration-200 shadow-lg hover:shadow-orange-200 text-lg"
          >
            Demander un devis gratuit
            <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default QuiSommesNousPage;
